import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { CatalogBootstrap } from "@/components/CatalogBootstrap";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { useApp } from "@/store/useApp";
import { staffEntryRoute } from "@/lib/staffWelcome";
import Login from "./pages/Login";
import ForcePasswordReset from "./pages/ForcePasswordReset";
import AccountRecovery from "./pages/AccountRecovery";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import Terms from "./pages/legal/Terms";
import Onboarding from "./pages/Onboarding";
import NoAccess from "./pages/NoAccess";
import Catalog from "./pages/Catalog";
import Checkout from "./pages/Checkout";
import Orders from "./pages/Orders";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import { AppLayout } from "./layouts/AppLayout";
import { AdminLayout } from "./layouts/AdminLayout";
import { DriverLayout } from "./layouts/DriverLayout";
import DriverOrders from "./pages/driver/DriverOrders";
import DriverConfirmDelivery from "./pages/driver/DriverConfirmDelivery";
import CandidatePortal from "./pages/selection/CandidatePortal";
import HelpCenter from "./pages/help/HelpCenter";
import { NexoHelp } from "./components/nexo/NexoHelp";

const queryClient = new QueryClient();

/**
 * Mantém a sessão já aberta em sincronia com o backend: revalida /auth/me (silenciosamente, sem mexer
 * no carrinho) ao navegar entre rotas e ao a janela/aba reganhar foco. Combinado com o resync em 403
 * (lib/api.ts), menus, rotas e botões refletem mudanças de acesso sem recarregar a página.
 */
function SessionSync() {
  const refreshAccount = useApp((state) => state.refreshAccount);
  const location = useLocation();

  useEffect(() => {
    if (useApp.getState().account) void refreshAccount();
  }, [location.pathname, refreshAccount]);

  useEffect(() => {
    const resync = () => {
      if (useApp.getState().account) void refreshAccount();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") resync();
    };
    window.addEventListener("focus", resync);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", resync);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshAccount]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner position="top-center" />
      <AppShell />
    </TooltipProvider>
  </QueryClientProvider>
);

function AppShell() {
  const account = useApp((state) => state.account);
  const authStatus = useApp((state) => state.authStatus);
  const supplierNfEnabled = useApp((state) => state.supplierNfEnabled);
  const bootstrapAuth = useApp((state) => state.bootstrapAuth);
  const loadModuleFlags = useApp((state) => state.loadModuleFlags);

  useEffect(() => {
    void bootstrapAuth();
    void loadModuleFlags();
  }, [bootstrapAuth, loadModuleFlags]);

  if (authStatus === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span>Validando sessão...</span>
        </div>
      </div>
    );
  }

  // Entrada no ambiente: funcionário pousa na tela de boas-vindas (ou no Dashboard, se for a preferência
  // dele neste navegador); cliente/entregador seguem como antes. Respeita a flag de NF ao resolver.
  const initialRoute = staffEntryRoute(account, supplierNfEnabled);

  return (
    <BrowserRouter>
      <CatalogBootstrap />
      <SessionSync />
      <Routes>
        <Route path="/" element={<Navigate to={initialRoute} replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/redefinir-senha" element={<ForcePasswordReset />} />
        <Route path="/recuperar-acesso" element={<AccountRecovery />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/sem-acesso" element={<NoAccess />} />
        <Route path="/selecao" element={<CandidatePortal />} />

        <Route element={<AppLayout />}>
          <Route path="/catalogo" element={<Catalog />} />
          <Route path="/processados" element={<Catalog processedOnly />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/pedidos" element={<Orders />} />
          <Route path="/perfil" element={<Profile />} />
          <Route path="/ajuda" element={<HelpCenter />} />
        </Route>

        {/* AdminLayout monta a navegação por abas internas; as rotas reais vivem em AdminRoutes e são
            renderizadas POR ABA (keep-alive). Os placeholders splat só fazem o layout casar /admin/* e
            /dashboard/* — o conteúdo não passa pelo Outlet. */}
        <Route element={<AdminLayout />}>
          <Route path="/admin/*" element={null} />
          <Route path="/dashboard/*" element={null} />
        </Route>

        <Route element={<DriverLayout />}>
          <Route path="/entregador" element={<DriverOrders />} />
          <Route path="/entregador/pedidos/:id" element={<DriverConfirmDelivery />} />
          <Route path="/entregador/ajuda" element={<HelpCenter />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* Mascote Nexo: instância ÚNICA do mecanismo de ajuda, nos três ambientes, fora do keep-alive de abas. */}
      <NexoHelp />
    </BrowserRouter>
  );
}

export default App;
